import { Component } from '@theme/component';
import { clamp, mediaQueryHover } from '@theme/utilities';

const DEFAULT_MAGNIFICATION = 2.5;
// A touch that moves less than this is a tap (toggles zoom), anything more is a drag/swipe/scroll.
const TAP_MOVE_THRESHOLD = 10;

/**
 * A custom element that magnifies its image on hover, panning a magnified crop of the
 * image across the same bounds the image itself occupies (no floating lens, no side panel).
 *
 * On touch, a tap toggles zoom and a one-finger drag pans while zoomed (#92). Press-and-hold was
 * dropped: Safari on iPad takes a long-press on an image as a right-click. With zoom off, touches
 * are left alone, so the carousel still swipes and the page still scrolls.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overlay - The magnified crop overlay, filling the same bounds as the image.
 *
 * @extends {Component<Refs>}
 */
export class ImageMagnifier extends Component {
  requiredRefs = ['overlay'];

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {AbortController} */
  #touchController = new AbortController();

  /** @type {boolean} */
  #highResLoaded = false;

  /** @type {{ x: number, y: number } | null} */
  #touchStartPosition = null;

  /** @type {boolean} */
  #touchMoved = false;

  connectedCallback() {
    super.connectedCallback();
    mediaQueryHover.addEventListener('change', this.#handleHoverCapabilityChange);
    this.#syncListeners();
    this.#touchController = new AbortController();
    this.#initTouchListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryHover.removeEventListener('change', this.#handleHoverCapabilityChange);
    this.#controller.abort();
    this.#touchController.abort();
  }

  #handleHoverCapabilityChange = () => {
    this.#syncListeners();
  };

  /**
   * Devices without a fine, hover-capable pointer never get listeners attached. Re-evaluated
   * whenever the device's hover capability changes (e.g. a mouse is connected/disconnected).
   */
  #syncListeners() {
    this.#controller.abort();

    if (!mediaQueryHover.matches) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
    this.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
  }

  /**
   * Touch listeners are always attached, independent of hover capability, so tap-to-zoom works on
   * touch devices regardless of whether they also report `(hover: hover)`.
   */
  #initTouchListeners() {
    const { signal } = this.#touchController;
    const options = { passive: true, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    // Not passive: while zoomed, a drag pans the crop instead of scrolling the page or carousel.
    this.addEventListener('touchmove', this.#handleTouchMove, { passive: false, signal });
    // Not passive: a tap's synthetic click is cancelled so it doesn't also act on the page.
    this.addEventListener('touchend', this.#handleTouchEnd, { passive: false, signal });
    this.addEventListener('touchcancel', this.#handleTouchCancel, options);
    // Some browsers (e.g. Android Chrome) open a "save image" menu on long-press even while
    // panning; suppress it while zoomed.
    this.addEventListener('contextmenu', this.#handleContextMenu, { signal });
  }

  get #isTouchZoomed() {
    return this.hasAttribute('touch-zoomed');
  }

  /** @param {Event} event */
  #handleContextMenu = (event) => {
    if (this.#isTouchZoomed) event.preventDefault();
  };

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    const touch = event.touches[0];
    if (!touch || event.touches.length > 1) {
      // A second finger (pinch, incidental brace) is never a tap.
      this.#touchStartPosition = null;
      return;
    }

    this.#touchStartPosition = { x: touch.clientX, y: touch.clientY };
    this.#touchMoved = false;
    // Zoomed: pan from the first press, no hold.
    if (this.#isTouchZoomed) this.#updatePosition(touch);
  };

  /** @param {TouchEvent} event */
  #handleTouchMove = (event) => {
    const touch = event.touches[0];
    if (!touch) return;

    if (this.#touchStartPosition && getDistance(touch, this.#touchStartPosition) > TAP_MOVE_THRESHOLD) {
      this.#touchMoved = true;
    }

    if (this.#isTouchZoomed) {
      event.preventDefault();
      this.#updatePosition(touch);
    }
  };

  /** @param {TouchEvent} event */
  #handleTouchEnd = (event) => {
    if (event.touches.length > 0 || !this.#touchStartPosition) return;

    const isTap = !this.#touchMoved;
    const touch = event.changedTouches[0];
    this.#touchStartPosition = null;
    if (!isTap) return;

    // The tap's synthetic mouse events and click mustn't also reach the page.
    if (event.cancelable) event.preventDefault();

    if (this.#isTouchZoomed) {
      this.removeAttribute('touch-zoomed');
      this.removeAttribute('magnifying');
    } else {
      this.#loadHighResImage();
      this.toggleAttribute('touch-zoomed', true);
      this.toggleAttribute('magnifying', true);
      if (touch) this.#updatePosition(touch);
    }
  };

  #handleTouchCancel = () => {
    this.#touchStartPosition = null;
  };

  get #image() {
    return /** @type {HTMLImageElement | null} */ (this.querySelector('.product-media__image'));
  }

  get #magnification() {
    const value = Number(this.dataset.magnification);
    return Number.isFinite(value) && value > 0 ? value : DEFAULT_MAGNIFICATION;
  }

  /** @param {PointerEvent} event */
  #handlePointerEnter = (event) => {
    // Hybrid devices report hover support but still send touch pointer events; touch has its own handling.
    if (event.pointerType === 'touch') return;
    this.#loadHighResImage();
    this.toggleAttribute('magnifying', true);
    this.#updatePosition(event);
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (event.pointerType === 'touch' || this.#isTouchZoomed || !this.hasAttribute('magnifying')) return;
    this.#updatePosition(event);
  };

  /** @param {PointerEvent} event */
  #handlePointerLeave = (event) => {
    if (event.pointerType === 'touch' || this.#isTouchZoomed) return;
    this.removeAttribute('magnifying');
  };

  /**
   * Lazily loads the high-resolution source (already present on the image via
   * `data_max_resolution`, the same attribute the old fullscreen dialog used) on first hover.
   */
  #loadHighResImage() {
    if (this.#highResLoaded) return;

    const highResUrl = this.#image?.getAttribute('data_max_resolution');
    if (!highResUrl) return;

    this.refs.overlay.style.backgroundImage = `url("${highResUrl}")`;
    this.#highResLoaded = true;
  }

  /**
   * Positions the magnified crop so it's centered under the pointer, scaled by the
   * magnification factor. Background-position percentages naturally clamp the crop to the
   * image's own edges: 0%/100% map exactly to the background image's edges.
   *
   * @param {PointerEvent | Touch} event
   */
  #updatePosition(event) {
    const image = this.#image;
    if (!image) return;

    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const relativeX = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const relativeY = clamp((event.clientY - rect.top) / rect.height, 0, 1);

    // The image's rendered box (rect) can have a different aspect ratio than the image's
    // natural dimensions (the gallery container forces its own aspect-ratio via CSS). A plain
    // `${magnification * 100}% ${magnification * 100}%` background-size scales the image
    // independently on each axis in that case, distorting it. Scale both axes by the same
    // factor instead - matching `background-size: cover` at 1x - so magnification never
    // stretches the image.
    const naturalWidth = image.naturalWidth || rect.width;
    const naturalHeight = image.naturalHeight || rect.height;
    const coverScale = Math.max(rect.width / naturalWidth, rect.height / naturalHeight);
    const widthPercent = ((naturalWidth * coverScale * this.#magnification) / rect.width) * 100;
    const heightPercent = ((naturalHeight * coverScale * this.#magnification) / rect.height) * 100;

    this.refs.overlay.style.backgroundSize = `${widthPercent}% ${heightPercent}%`;
    this.refs.overlay.style.backgroundPosition = `${relativeX * 100}% ${relativeY * 100}%`;
  }
}

/**
 * @param {{ clientX: number, clientY: number }} point1
 * @param {{ x: number, y: number }} point2
 * @returns {number}
 */
function getDistance(point1, point2) {
  const dx = point1.clientX - point2.x;
  const dy = point1.clientY - point2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('image-magnifier')) {
  customElements.define('image-magnifier', ImageMagnifier);
}
